import { DrawerProps } from "antd";
import { Form as AntForm } from "antd";
import Drawer from "components/common/Drawer";
import {
  ERROR_PRODUCT_DELETED,
  ERROR_SOMETHING_WENT_WRONG,
  PRODUCT_DETAILS_DRAWER_TITLE,
  PRODUCT_SAVE_SUCCESS,
  SELECT_VENUE_WARNING,
  SUCCESS_PRODUCT_DELETED,
} from "constants/strings";
import DrawerHeader from "pages/Menus/DrawerHeader";
import * as React from "react";
import ModalDelete from "components/ModalDelete";
import ProductDetailsForm from "./ProductDetailsForm";
import { Item } from "fetchers/item";
import message from "components/common/message";
import { VENUE_ITEM_POST, VENUE_ITEM_PATCH } from "constants/endpoints";
import Button from "components/form/Button";
import { CheckCircle, Trash2 } from "react-feather";
import { deleteItem, saveItem } from "services/itemApi";
import { useItemsStore } from "stores/itemsStore";

type Props = DrawerProps & {
  item?: Item;
  isNew?: boolean;
  isVisible: boolean;
  onClose?: () => void;
};

const EditCreateProductDrawer: React.FC<Props> = (props) => {
  const { item, isNew, isVisible, onClose, ...restProps } = props;
  const [form] = AntForm.useForm();
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isItemVisible, setIsItemVisible] = React.useState(props.item?.visible ?? true);

  const [, actions] = useItemsStore();

  const handleDelete = () => {
    setShowDeleteModal(false);

    if (props.isNew) {
      return;
    }

    if (!item?.venueId || !item?.id) {
      return;
    }

    setIsLoading(true);

    deleteItem(item)
      .then(() => {
        actions.deleteItem && props.item && actions.deleteItem(props.item);
        message({
          type: "success",
          content: SUCCESS_PRODUCT_DELETED,
          progressBar: false,
          duration: 3,
          key: 1,
        });
        props.onClose && props.onClose();
      })
      .catch(() => {
        message({
          type: "warning",
          content: ERROR_PRODUCT_DELETED,
          progressBar: false,
          duration: 6,
          key: 1,
        });
      })
      .finally(() => setIsLoading(false));
  };

  const handleSave = (data: Item) => {
    let endpoint;

    if (isLoading) {
      setIsLoading(false);
    }

    if (!data?.venueId) {
      message({
        type: "warning",
        content: SELECT_VENUE_WARNING,
        progressBar: false,
        duration: 3,
        key: 1,
      });
      return;
    }

    // Don't send image uri
    data.imageUri = undefined;

    if (props.isNew) {
      endpoint = VENUE_ITEM_POST(data.venueId);
    } else if (props.item?.id) {
      endpoint = VENUE_ITEM_PATCH(data.venueId, props.item.id);
    } else {
      message({
        type: "warning",
        content: "Product doesn't exist id: " + props.item?.id,
        progressBar: false,
        duration: 3,
        key: 1,
      });
      return;
    }

    setIsLoading(true);

    data.visible = isItemVisible;

    saveItem(endpoint, props.isNew ? "POST" : "PATCH", data)
      .then((response) => {
        if (props.isNew) {
          actions.addItem && actions.addItem(response.data as Item);
        } else {
          actions.updateItems && actions.updateItems(response.data as Item[] | []);
        }
        message({
          type: "success",
          content: PRODUCT_SAVE_SUCCESS,
          progressBar: false,
          duration: 3,
          key: 1,
        });
        props.onClose && props.onClose();
      })
      .catch((err) => {
        console.error(err);
        message({
          type: "warning",
          content: ERROR_SOMETHING_WENT_WRONG,
          progressBar: false,
          duration: 6,
          key: 1,
        });
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      <Drawer
        onClose={() => {
          form.resetFields();
          onClose && onClose();
        }}
        visible={isVisible}
        key={item?.id}
        title={
          <DrawerHeader
            isNew={isNew}
            isVisible={isItemVisible}
            title={PRODUCT_DETAILS_DRAWER_TITLE}
            onVisibilityChange={(isVisible) => setIsItemVisible(isVisible)}
          />
        }
        footer={
          <div className="flex gap-6 justify-between">
            <Button
              size="large"
              color="red"
              htmlType="button"
              onClick={() => setShowDeleteModal(true)}
              disabled={props.isNew}
            >
              Delete
              <Trash2 size={24} className="ml-10" />
            </Button>

            <Button size="large" color="black" onClick={() => form.submit()} htmlType="submit">
              Publish
              <CheckCircle size={24} className="ml-10" />
            </Button>
          </div>
        }
        {...restProps}
      >
        <ProductDetailsForm
          item={item}
          isNew={isNew}
          form={form}
          onFormFinish={handleSave}
          closeDrawer={() => onClose && onClose()}
          onDelete={() => setShowDeleteModal(true)}
        />
      </Drawer>
      <ModalDelete
        visible={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onClose={() => setShowDeleteModal(false)}
        onOk={handleDelete}
      />
    </>
  );
};

EditCreateProductDrawer.displayName = "ProductDetailsDrawer";

export default EditCreateProductDrawer;
